import React, { useState, useEffect } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { requestPasswordReset } from '../../services/api';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, ArrowRight, AlertCircle, ChevronLeft, Sparkles } from 'lucide-react';
import '../auth/AuthForms.scss';

const PasswordResetRequest = () => {
  const { t } = useTranslation(['auth', 'common']);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const PasswordResetSchema = Yup.object().shape({
    email: Yup.string()
      .email(t('errors.email_invalid'))
      .required(t('errors.email_required')),
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 600);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      await requestPasswordReset(values.email);
      setSubmitted(true);
      setError(null);
    } catch (err) {
      setError(t('errors.connection_error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="auth-loading">
        <motion.div 
          animate={{ scale: [1, 1.1, 1], opacity: [0.5, 1, 0.5] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="loading-logo"
        >
          FOTH
        </motion.div>
      </div>
    );
  }

  return (
    <div className="auth-page editorial-split">
      {/* Editorial Side */}
      <div className="auth-editorial-side">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.5 }}
            className="editorial-bg-overlay"
          />
          <div className="editorial-content">
            <Link to="/" className="auth-logo-inverse">FOTH</Link>
            <div className="editorial-text">
               <motion.h1
                 initial={{ opacity: 0, y: 20 }}
                 animate={{ opacity: 1, y: 0 }}
                 transition={{ duration: 0.8 }}
               >
                 {t('forgot_password.editorial.title')}
               </motion.h1>
               <motion.p
                 initial={{ opacity: 0 }}
                 animate={{ opacity: 1 }}
                 transition={{ delay: 0.3 }}
               >
                 {t('forgot_password.editorial.text')}
               </motion.p>
            </div>
          </div>
      </div>

      {/* Form Side */}
      <div className="auth-form-side">
        <div className="auth-container">
          <AnimatePresence mode="wait">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="auth-card-modern"
            >
              <div className="auth-header">
                <Link to="/login" className="back-link">
                  <ChevronLeft size={16} /> {t('common:buttons.back')}
                </Link>
                <h2 className="auth-title">{t('forgot_password.title')}</h2>
                <p className="auth-subtitle">{t('forgot_password.subtitle')}</p>
              </div>
              
              <div className="auth-body">
                {submitted ? (
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                  >
                    <div className="alert-premium success column-layout">
                      <Sparkles size={32} />
                      <p>{t('forgot_password.success')}</p>
                    </div>
                    <Link to="/login" className="auth-submit-btn-premium mt-6 no-underline" style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5rem' }}>
                       {t('forgot_password.footer.back_to_login')}
                    </Link>
                  </motion.div>
                ) : (
                  <>
                    {error && (
                      <div className="alert-premium error" role="alert">
                        <AlertCircle size={18} /> {error}
                      </div>
                    )}
                    
                    <Formik
                      initialValues={{ email: '' }}
                      validationSchema={PasswordResetSchema}
                      onSubmit={handleSubmit}
                    >
                      {({ isSubmitting }) => (
                        <Form className="auth-form">
                          <div className="form-group-full">
                            <label className="form-label">{t('forgot_password.form.email')}</label>
                            <div className="input-premium-wrapper">
                              <Mail size={18} className="input-icon" />
                              <Field
                                type="email"
                                name="email"
                                className="form-control-premium"
                                placeholder={t('forgot_password.form.placeholder')}
                              />
                            </div>
                            <ErrorMessage name="email" component="div" className="error-message" />
                          </div>

                          <motion.button
                            type="submit"
                            className="auth-submit-btn-premium"
                            disabled={isSubmitting}
                            whileTap={{ scale: 0.98 }}
                          >
                            {isSubmitting ? (
                              <span className="btn-spinner" />
                            ) : (
                              <>{t('forgot_password.submit')} <ArrowRight size={18} /></>
                            )}
                          </motion.button>
                        </Form>
                      )}
                    </Formik>
                  </>
                )}
              </div>
              
              <div className="auth-footer">
                <p><Link to="/login">{t('forgot_password.footer.back_to_login')}</Link></p>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default PasswordResetRequest;